import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { PageFlip } from 'page-flip';
import { FiChevronLeft, FiChevronRight, FiMaximize2, FiMinimize2, FiZoomIn, FiZoomOut } from 'react-icons/fi';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

// Shimmer styles injected once. DOM-created placeholders need a real CSS class
// because inline @keyframes don't work.
const STYLE_ID = 'pdf-flipbook-styles';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .flip-placeholder {
      position: absolute;
      inset: 0;
      background: linear-gradient(110deg, #ececec 8%, #f6f6f6 22%, #ececec 36%);
      background-size: 200% 100%;
      animation: flip-shimmer 1.6s infinite linear;
    }
    .flip-placeholder::after {
      content: attr(data-page);
      position: absolute;
      bottom: 8px;
      right: 12px;
      font: 11px/1 system-ui, sans-serif;
      color: #c4c4c4;
      letter-spacing: 0.04em;
    }
    @keyframes flip-shimmer {
      from { background-position: 100% 0; }
      to   { background-position: -100% 0; }
    }
  `;
  document.head.appendChild(style);
}

const RENDER_SCALE = 2.5;
const PREVIEW_SCALE = 0.4;
const MAX_CONCURRENT = 2;
const BACKDROP = '#2c2c2c';
const RANGE_CHUNK_SIZE = 65536;
const HIGH_RES_DELAY_MS = 900;
// How many spreads to prefetch ahead/behind the current one. Previews-only,
// so cost is small; gives flips a much smoother feel.
const LOOKAHEAD_SPREADS = 2;
const LOOKBEHIND_SPREADS = 1;

class FetchRangeTransport extends pdfjsLib.PDFDataRangeTransport {
  constructor(url, length) {
    // Mark progressive loading as done so pdfjs does not wait for a full-reader
    // stream. All bytes are supplied through explicit Range requests below.
    super(length, undefined, true);
    this.url = url;
    this.controllers = new Set();
    this.cache = new Map();
    this.inflight = new Map();
  }

  requestDataRange(begin, end) {
    const key = `${begin}-${end}`;
    const cached = this.cache.get(key);
    if (cached) {
      // eslint-disable-next-line no-console
      console.log('[PdfFlipbook] cache HIT', key);
      this.onDataRange(begin, cached.slice());
      this.onDataProgress(Math.min(end, this.length), this.length);
      return;
    }

    const existing = this.inflight.get(key);
    if (existing) {
      // eslint-disable-next-line no-console
      console.log('[PdfFlipbook] in-flight dedup', key);
      existing.then((chunk) => {
        if (!chunk) return;
        this.onDataRange(begin, chunk.slice());
        this.onDataProgress(Math.min(end, this.length), this.length);
      });
      return;
    }
    // eslint-disable-next-line no-console
    console.log('[PdfFlipbook] cache MISS, fetching', key, `(${(end - begin) / 1024} KB)`);

    const controller = new AbortController();
    this.controllers.add(controller);

    const request = fetch(this.url, {
      headers: { Range: `bytes=${begin}-${end - 1}` },
      signal: controller.signal,
    })
      .then((response) => {
        if (response.status !== 206) {
          throw new Error(`Expected HTTP 206 for PDF range request, got ${response.status}`);
        }
        return response.arrayBuffer();
      })
      .then((buffer) => {
        const chunk = new Uint8Array(buffer);
        this.cache.set(key, chunk);
        this.onDataRange(begin, chunk.slice());
        this.onDataProgress(Math.min(end, this.length), this.length);
        return chunk;
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('PDF range request failed', err);
        }
        return null;
      })
      .finally(() => {
        this.controllers.delete(controller);
        this.inflight.delete(key);
      });

    this.inflight.set(key, request);
  }

  abort() {
    this.controllers.forEach((controller) => controller.abort());
    this.controllers.clear();
    this.inflight.clear();
  }
}

const getRangeCapablePdfLength = async (url) => {
  const head = await fetch(url, { method: 'HEAD' });
  if (!head.ok) return undefined;

  const contentLength = Number.parseInt(head.headers.get('content-length'), 10);
  const acceptRanges = head.headers.get('accept-ranges');
  const contentEncoding = head.headers.get('content-encoding') || 'identity';

  if (!Number.isInteger(contentLength) || contentLength <= 0) return undefined;
  // Use includes(), not strict equality — proxies (e.g., Cloudflare) sometimes
  // duplicate the Accept-Ranges header so headers.get() returns "bytes, bytes".
  if (!acceptRanges || !acceptRanges.toLowerCase().includes('bytes')) return undefined;
  if (contentEncoding.toLowerCase() !== 'identity') return undefined;

  return contentLength;
};

// "16-17 / 232" when on a spread, "1 / 232" or "232 / 232" for solo pages.
const formatPageLabel = (current, total) => {
  if (!total) return '';
  if (current === 0) return `1 / ${total}`;
  if (total % 2 === 0 && current === total - 1) return `${total} / ${total}`;
  const left = current % 2 === 1 ? current : current - 1;
  return `${left + 1}-${left + 2} / ${total}`;
};

const getVisiblePageIndices = (current, total) => {
  if (!total) return [];
  if (current <= 0) return [0];
  if (total % 2 === 0 && current >= total - 1) return [total - 1];

  const left = current % 2 === 1 ? current : current - 1;
  return [left, left + 1].filter((idx) => idx >= 0 && idx < total);
};

const PdfFlipbook = ({ url, width = 500, height = 700 }) => {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const viewportRef = useRef(null);
  const flipRef = useRef(null);
  const docRef = useRef(null);
  const renderingRef = useRef(new Set());
  const renderedRef = useRef(new Set());
  const previewRenderedRef = useRef(new Set());
  // Queue holds { idx, scale } entries for the currently visible page/spread.
  const queueRef = useRef([]);
  const concurrentRef = useRef(0);
  const queueWindowRef = useRef(() => {});

  const [status, setStatus] = useState('loading');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let loadingTask = null;
    let rangeTransport = null;
    let highResTimer = null;
    let docPromise = null;
    const containerNode = containerRef.current;
    const renderedPages = renderedRef.current;
    const previewRenderedPages = previewRenderedRef.current;
    const renderingPages = renderingRef.current;

    const loadPdfDocument = async () => {
      if (docRef.current) return docRef.current;
      if (docPromise) return docPromise;

      docPromise = (async () => {
        let length;
        try {
          length = await getRangeCapablePdfLength(url);
        } catch (e) { /* fall through; pdfjs will handle without custom range transport */ }
        if (cancelled) return null;

        // eslint-disable-next-line no-console
        console.log('[PdfFlipbook] setup', { url, length, transportMode: length ? 'FetchRangeTransport' : 'pdfjs default (URL)' });

        const source = {
          rangeChunkSize: RANGE_CHUNK_SIZE,
          disableAutoFetch: true,
        };

        if (length) {
          rangeTransport = new FetchRangeTransport(url, length);
          source.range = rangeTransport;
        } else {
          source.url = url;
        }

        loadingTask = pdfjsLib.getDocument(source);
        const doc = await loadingTask.promise;
        if (cancelled) return null;
        docRef.current = doc;
        return doc;
      })();

      return docPromise;
    };

    const renderPage = async (idx, scale) => {
      if (cancelled) return;
      const isPreview = scale < RENDER_SCALE;
      if (renderedRef.current.has(idx)) return;
      if (isPreview && previewRenderedRef.current.has(idx)) return;

      const key = `${idx}:${scale}`;
      if (renderingRef.current.has(key)) return;
      const total = docRef.current ? docRef.current.numPages : 0;
      if (idx < 0 || idx >= total) return;

      renderingRef.current.add(key);
      try {
        const doc = await loadPdfDocument();
        if (!doc || cancelled) return;
        const page = await doc.getPage(idx + 1);
        if (cancelled) return;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        if (cancelled) return;

        const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, 'image/jpeg', isPreview ? 0.75 : 0.9)
        );
        if (cancelled || !blob) return;

        const pageEl = containerRef.current && containerRef.current.querySelector(`[data-page-index="${idx}"]`);
        if (pageEl) {
          const img = pageEl.querySelector('img');
          if (!isPreview || !renderedRef.current.has(idx)) {
            const blobUrl = URL.createObjectURL(blob);
            const oldSrc = img.dataset.blobUrl;
            if (oldSrc) URL.revokeObjectURL(oldSrc);
            img.src = blobUrl;
            img.dataset.blobUrl = blobUrl;
            const placeholder = pageEl.querySelector('.flip-placeholder');
            if (placeholder) placeholder.remove();
          }
        }
        if (isPreview) previewRenderedRef.current.add(idx);
        else renderedRef.current.add(idx);
      } catch (err) {
        console.error('Render failed for page', idx + 1, '@', scale, err);
      } finally {
        renderingRef.current.delete(key);
      }
    };

    const pump = () => {
      while (
        !cancelled &&
        concurrentRef.current < MAX_CONCURRENT &&
        queueRef.current.length > 0
      ) {
        const entry = queueRef.current.shift();
        const { idx, scale } = entry;
        const isPreview = scale < RENDER_SCALE;
        if (renderedRef.current.has(idx)) continue;
        if (isPreview && previewRenderedRef.current.has(idx)) continue;
        concurrentRef.current++;
        renderPage(idx, scale).finally(() => {
          concurrentRef.current--;
          pump();
        });
      }
    };

    const queueWindow = (centerIdx) => {
      if (highResTimer) {
        clearTimeout(highResTimer);
        highResTimer = null;
      }

      const total = docRef.current ? docRef.current.numPages : 0;
      const visiblePages = getVisiblePageIndices(centerIdx, total);
      // Build ordered list: visible spread first (highest priority), then
      // lookahead spreads, then lookbehind. All at preview scale — they're
      // cheap, and render-on-demand high-res happens for the visible spread.
      const order = [...visiblePages];
      const SPREAD = 2;
      for (let s = 1; s <= LOOKAHEAD_SPREADS; s++) {
        for (const idx of getVisiblePageIndices(centerIdx + s * SPREAD, total)) {
          if (!order.includes(idx)) order.push(idx);
        }
      }
      for (let s = 1; s <= LOOKBEHIND_SPREADS; s++) {
        for (const idx of getVisiblePageIndices(centerIdx - s * SPREAD, total)) {
          if (!order.includes(idx)) order.push(idx);
        }
      }

      const next = [];
      for (const idx of order) {
        if (renderedRef.current.has(idx)) continue;
        if (previewRenderedRef.current.has(idx)) continue;
        next.push({ idx, scale: PREVIEW_SCALE });
      }
      // Replace queue: stale prefetches dropped, in-flight renders finish.
      queueRef.current = next;
      pump();

      highResTimer = setTimeout(() => {
        // After the brief settle delay, upgrade ONLY the currently visible
        // pages to high-res (don't waste work on lookahead pages).
        for (const idx of visiblePages) {
          if (renderedRef.current.has(idx)) continue;
          queueRef.current.push({ idx, scale: RENDER_SCALE });
        }
        pump();
      }, HIGH_RES_DELAY_MS);
    };
    queueWindowRef.current = queueWindow;

    const setup = async () => {
      try {
        setStatus('loading');
        const doc = await loadPdfDocument();
        if (cancelled) return;
        const numPages = doc.numPages;
        setTotalPages(numPages);

        const container = containerRef.current;
        container.innerHTML = '';
        for (let i = 0; i < numPages; i++) {
          const pageEl = document.createElement('div');
          pageEl.className = 'flip-page';
          pageEl.style.background = '#fff';
          pageEl.style.position = 'relative';
          pageEl.dataset.pageIndex = String(i);

          const img = document.createElement('img');
          img.style.width = '100%';
          img.style.height = '100%';
          img.style.objectFit = 'contain';
          img.style.display = 'block';
          pageEl.appendChild(img);

          const placeholder = document.createElement('div');
          placeholder.className = 'flip-placeholder';
          placeholder.dataset.page = String(i + 1);
          pageEl.appendChild(placeholder);

          container.appendChild(pageEl);
        }

        flipRef.current = new PageFlip(container, {
          width,
          height,
          size: 'stretch',
          minWidth: 315,
          maxWidth: 2400,
          minHeight: 420,
          maxHeight: 2400,
          drawShadow: true,
          showCover: true,
          mobileScrollSupport: true,
          usePortrait: true,
        });
        flipRef.current.loadFromHTML(container.querySelectorAll('.flip-page'));
        flipRef.current.on('flip', (e) => {
          setCurrentPage(e.data);
          queueWindow(e.data);
        });
        setStatus('ready');
        setCurrentPage(0);
        queueWindow(0);
      } catch (err) {
        console.error('PdfFlipbook setup error', err);
        if (!cancelled) setStatus('error');
      }
    };

    setup();

    return () => {
      cancelled = true;
      if (highResTimer) {
        clearTimeout(highResTimer);
        highResTimer = null;
      }
      if (flipRef.current) {
        try { flipRef.current.destroy(); } catch (e) { /* noop */ }
        flipRef.current = null;
      }
      if (loadingTask) {
        try { loadingTask.destroy(); } catch (e) { /* noop */ }
        loadingTask = null;
      }
      if (rangeTransport) {
        try { rangeTransport.abort(); } catch (e) { /* noop */ }
        rangeTransport = null;
      }
      if (containerNode) {
        containerNode.querySelectorAll('img').forEach((img) => {
          if (img.dataset.blobUrl) URL.revokeObjectURL(img.dataset.blobUrl);
        });
      }
      if (docRef.current) {
        try { docRef.current.destroy(); } catch (e) { /* noop */ }
        docRef.current = null;
      }
      renderedPages.clear();
      previewRenderedPages.clear();
      renderingPages.clear();
    };
  }, [url, width, height]);

  // Wheel-to-zoom over the viewport, but only in fullscreen (issuu behavior).
  // Outside fullscreen the page scrolls normally when the wheel turns over the
  // flipbook.
  useEffect(() => {
    if (!isFullscreen) return undefined;
    const vp = viewportRef.current;
    if (!vp) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const direction = e.deltaY < 0 ? 1 : -1;
      setZoom((z) => {
        const next = +(z + direction * 0.1).toFixed(2);
        return Math.max(1, Math.min(2.5, next));
      });
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, [isFullscreen]);

  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      // page-flip in 'stretch' mode listens for resize; nudge it after the
      // fullscreen transition so it re-measures the container.
      requestAnimationFrame(() => {
        if (flipRef.current && typeof flipRef.current.update === 'function') {
          flipRef.current.update();
        }
        window.dispatchEvent(new Event('resize'));
      });
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const goPrev = () => flipRef.current && flipRef.current.flipPrev();
  const goNext = () => flipRef.current && flipRef.current.flipNext();
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      wrapperRef.current && wrapperRef.current.requestFullscreen && wrapperRef.current.requestFullscreen();
    } else {
      document.exitFullscreen && document.exitFullscreen();
    }
  };

  // Click-and-drag panning when zoomed. At zoom=1 we let page-flip handle the
  // mouse so its corner-drag-to-flip gesture still works.
  const onPanStart = (e) => {
    if (zoom <= 1 || !viewportRef.current) return;
    e.preventDefault();
    const vp = viewportRef.current;
    const startX = e.clientX;
    const startY = e.clientY;
    const startSL = vp.scrollLeft;
    const startST = vp.scrollTop;
    setIsPanning(true);

    const onMove = (ev) => {
      vp.scrollLeft = startSL - (ev.clientX - startX);
      vp.scrollTop = startST - (ev.clientY - startY);
    };
    const onUp = () => {
      setIsPanning(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const navBtn = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 48,
    height: 48,
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(255,255,255,0.08)',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 28,
    transition: 'background 0.15s',
    zIndex: 5,
  };

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative',
        width: '100%',
        background: BACKDROP,
        padding: '32px 64px 64px',
        boxSizing: 'border-box',
        // Lock the total height so layout doesn't jump between loading state
        // and page-flip's measured book size.
        minHeight: isFullscreen ? '100vh' : height + 32 + 64,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {status === 'loading' && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#999',
            fontFamily: 'system-ui, sans-serif',
            fontSize: 13,
            zIndex: 2,
            pointerEvents: 'none',
          }}
        >
          Cargando documento…
        </div>
      )}
      {status === 'error' && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#e88',
            fontFamily: 'system-ui, sans-serif',
            fontSize: 13,
            zIndex: 2,
          }}
        >
          No se pudo cargar el documento.
        </div>
      )}

      <button
        onClick={goPrev}
        aria-label="Página anterior"
        style={{ ...navBtn, left: 12 }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.18)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
      >
        <FiChevronLeft />
      </button>
      <button
        onClick={goNext}
        aria-label="Página siguiente"
        style={{ ...navBtn, right: 12 }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.18)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
      >
        <FiChevronRight />
      </button>

      {/* Viewport: clips overflow when zoomed, captures pan gestures.
          Spacer inflates the layout box so scrolling actually works.
          page-flip lives on `containerRef` and always sees the same layout
          dimensions — only the visual is scaled. */}
      <div
        ref={viewportRef}
        onMouseDown={onPanStart}
        style={{
          width: '100%',
          maxWidth: isFullscreen ? '100%' : width * 2,
          height: isFullscreen ? 'auto' : height,
          flex: isFullscreen ? 1 : '0 0 auto',
          margin: '0 auto',
          overflow: zoom > 1 ? 'auto' : 'visible',
          cursor: zoom > 1 ? (isPanning ? 'grabbing' : 'grab') : 'auto',
          userSelect: zoom > 1 ? 'none' : 'auto',
        }}
      >
        <div
          style={{
            width: `${zoom * 100}%`,
            height: `${zoom * 100}%`,
          }}
        >
          <div
            ref={containerRef}
            style={{
              width: `${100 / zoom}%`,
              height: `${100 / zoom}%`,
              transform: zoom !== 1 ? `scale(${zoom})` : undefined,
              transformOrigin: 'top left',
            }}
          />
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.45)',
          color: '#ddd',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '10px 20px',
          fontSize: 13,
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <span style={{ minWidth: 90, whiteSpace: 'nowrap' }}>{formatPageLabel(currentPage, totalPages)}</span>
        <input
          type="range"
          min={0}
          max={Math.max(0, totalPages - 1)}
          value={currentPage}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            setCurrentPage(v);
            if (flipRef.current && flipRef.current.turnToPage) {
              flipRef.current.turnToPage(v);
            }
            queueWindowRef.current(v);
          }}
          style={{
            flex: 1,
            accentColor: '#fff',
            cursor: 'pointer',
          }}
          disabled={totalPages === 0}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))}
            aria-label="Reducir zoom"
            disabled={zoom <= 1}
            style={{
              background: 'transparent',
              border: 'none',
              color: zoom <= 1 ? '#666' : '#ddd',
              cursor: zoom <= 1 ? 'default' : 'pointer',
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              padding: 4,
            }}
          >
            <FiZoomOut />
          </button>
          <input
            type="range"
            min={1}
            max={2.5}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
            style={{ width: 90, accentColor: '#fff', cursor: 'pointer' }}
            aria-label="Zoom"
          />
          <button
            onClick={() => setZoom((z) => Math.min(2.5, +(z + 0.25).toFixed(2)))}
            aria-label="Aumentar zoom"
            disabled={zoom >= 2.5}
            style={{
              background: 'transparent',
              border: 'none',
              color: zoom >= 2.5 ? '#666' : '#ddd',
              cursor: zoom >= 2.5 ? 'default' : 'pointer',
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              padding: 4,
            }}
          >
            <FiZoomIn />
          </button>
        </div>
        <button
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#ddd',
            cursor: 'pointer',
            fontSize: 18,
            display: 'flex',
            alignItems: 'center',
            padding: 6,
          }}
        >
          {isFullscreen ? <FiMinimize2 /> : <FiMaximize2 />}
        </button>
      </div>
    </div>
  );
};

export default PdfFlipbook;
